import React, { useState, useEffect } from "react";

// Define your initial structure in the context
const UserContext = React.createContext({
  userName: null,
  email: null,
  employeeCode: null,
  avatar: null,
  signature: null,
  userId: null,
  roleId: null,
  roleOverrides: false,
  overriddenRole: {
    can_access_all_company_scans: false,
    can_access_all_scans: false,
    can_access_expense: false,
    can_access_scans: false,
    can_add_documents: false,
    can_add_expense: false,
    can_add_invoice: false,
    can_delete_documents: false,
    can_delete_invoice: false,
    can_general_scan: false,
    can_manage_documents: false,
    can_settings: false,
    can_vendor: false,
    can_configurator: false,
    can_user_management: false,
    expense_inbox_access: false,
    invoice_inbox_access: false,
  },
  // Employee related data
  employeeData: {
    id: null,
    employee_code: null,
    employee_name: null,
    position_id: null,
    position_name: null,
    company_id: null,
    company_name: null,
    function_id: null,
    function_name: null,
    address: null,
    phone_number: null,
    email: null,
    location: null,
    designation: null,
    erp_code: null,
    cost_center: null,
    companies: [], // Array to hold associated companies
    functions: [], // Array to hold associated functions
  },
  setUserData: (userData) => {}, // Function to update all fields
  clearUserData: () => {}, // Function to clear user data
});

export const UserContextProvider = (props) => {
  const [userName, setUserName] = useState(null);
  const [email, setEmail] = useState(null);
  const [employeeCode, setEmployeeCode] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [signature, setSignature] = useState(null);
  const [userId, setUserId] = useState(null);
  const [roleId, setRoleId] = useState(null);
  const [roleOverrides, setRoleOverrides] = useState(false);
  const [overriddenRole, setOverriddenRole] = useState({
    can_access_all_company_scans: false,
    can_access_all_scans: false,
    can_access_expense: false,
    can_access_scans: false,
    can_add_documents: false,
    can_add_expense: false,
    can_add_invoice: false,
    can_delete_documents: false,
    can_delete_invoice: false,
    can_general_scan: false,
    can_manage_documents: false,
    can_settings: false,
    can_vendor: false,
    can_configurator: false,
    can_user_management: false,
    expense_inbox_access: false,
    invoice_inbox_access: false,
  });

  // Employee related state
  const [employeeData, setEmployeeData] = useState({
    id: null,
    employee_code: null,
    employee_name: null,
    position_id: null,
    position_name: null,
    company_id: null,
    company_name: null,
    function_id: null,
    function_name: null,
    address: null,
    phone_number: null,
    email: null,
    location: null,
    designation: null,
    erp_code: null,
    cost_center: null,
    companies: [], // Array to hold associated companies
    functions: [], // Array to hold associated functions
  });

  // Load cached avatar and signature from local storage on mount
  useEffect(() => {
    const cachedAvatar = localStorage.getItem("userAvatar");
    const cachedSignature = localStorage.getItem("userSignature");

    if (cachedAvatar) setAvatar(cachedAvatar);
    if (cachedSignature) setSignature(cachedSignature);
  }, []);

  const setUserData = (userData) => {
    setUserName(userData.name ?? userName);
    setEmail(userData.email ?? email);
    setEmployeeCode(userData.employee_code ?? employeeCode);
    
    // Only update avatar and signature if provided in userData
    if ("avatar" in userData) {
      setAvatar(userData.avatar);
      userData.avatar ? localStorage.setItem("userAvatar", userData.avatar) : localStorage.removeItem("userAvatar");
    }
    if ("signature" in userData) {
      setSignature(userData.signature);
      userData.signature ? localStorage.setItem("userSignature", userData.signature) : localStorage.removeItem("userSignature");
    }
  
    // Update the other user fields with existing values or new values from userData
    setUserId(userData.user_id ?? userId);
    setRoleId(userData.role_id ?? roleId);
    setRoleOverrides(userData.role_overrides ?? roleOverrides);
    setOverriddenRole({ ...overriddenRole, ...(userData.overridden_role || {}) });
    setEmployeeData({ ...employeeData, ...(userData.employee || {}) });
  };
  

  // Function to clear user data and local storage
  const clearUserData = () => {
    setUserName(null);
    setEmail(null);
    setEmployeeCode(null);
    setAvatar(null);
    setSignature(null);
    setUserId(null);
    setRoleId(null);
    setRoleOverrides(false);
    setOverriddenRole({
      can_access_all_company_scans: false,
      can_access_all_scans: false,
      can_access_expense: false,
      can_access_scans: false,
      can_add_documents: false,
      can_add_expense: false,
      can_add_invoice: false,
      can_delete_documents: false,
      can_delete_invoice: false,
      can_general_scan: false,
      can_manage_documents: false,
      can_settings: false,
      can_vendor: false,
      can_configurator: false,
      can_user_management: false,
      expense_inbox_access: false,
      invoice_inbox_access: false,
    });

    // Reset employee data
    setEmployeeData({
      id: null,
      employee_code: null,
      employee_name: null,
      position_id: null,
      position_name: null,
      company_id: null,
      company_name: null,
      function_id: null,
      function_name: null,
      address: null,
      phone_number: null,
      email: null,
      location: null,
      designation: null,
      erp_code: null,
      cost_center: null,
      companies: [],
      functions: [],
    });

    // Clear localStorage
    localStorage.removeItem("userAvatar");
    localStorage.removeItem("userSignature");
  };

  const contextValue = {
    userName,
    email,
    employeeCode,
    avatar,
    signature,
    userId,
    roleId,
    roleOverrides,
    overriddenRole,
    employeeData, // Expose employee data in context
    setUserData, // Function to update all data
    clearUserData, // Function to clear user data
  };

  return (
    <UserContext.Provider value={contextValue}>
      {props.children}
    </UserContext.Provider>
  );
};

export default UserContext;
